import React, { useMemo, useState } from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Space, Tag, Tabs, theme } from 'antd';
import { Field } from '../../components/Field';
import {
  MuscleGroup,
  ExerciseDifficultyLevel,
  ExerciseDto,
  ExerciseTranslationDto,
} from '@wellverse/shared-ts';
import { isDefaultLocale, useActiveLocales } from '../../hooks/useActiveLocales';

const muscleGroupColors: Record<string, string> = {
  [MuscleGroup.CHEST]: '#1890ff',
  [MuscleGroup.BACK]: '#722ed1',
  [MuscleGroup.SHOULDERS]: '#f5a623',
  [MuscleGroup.BICEPS]: '#3ecf8e',
  [MuscleGroup.TRICEPS]: '#ff4d4f',
  [MuscleGroup.LEGS]: '#eb2f96',
  [MuscleGroup.GLUTES]: '#faad14',
  [MuscleGroup.CORE]: '#52c41a',
  [MuscleGroup.FOREARMS]: '#2f54eb',
  [MuscleGroup.CALVES]: '#13c2c2',
  [MuscleGroup.FULL_BODY]: '#fa541c',
};

const muscleGroupColorList = Object.values(muscleGroupColors);

const difficultyColors: Record<string, string> = {
  [ExerciseDifficultyLevel.BEGINNER]: '#3ecf8e',
  [ExerciseDifficultyLevel.INTERMEDIATE]: '#f5a623',
  [ExerciseDifficultyLevel.ADVANCED]: '#ff4d4f',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

const LanguageContent: React.FC<{
  name?: string;
  description?: string;
  instructions?: string[];
  tips?: string[];
}> = ({ name, description, instructions, tips }) => (
  <div style={{ paddingTop: 8 }}>
    <Field label="Name">{name || '—'}</Field>
    <Field label="Description">
      {description ? (
        <div style={{ whiteSpace: 'pre-wrap' }}>{description}</div>
      ) : '—'}
    </Field>
    <Field label="Instructions">
      {(instructions || []).length > 0 ? (
        <ol style={{ paddingLeft: 20, margin: 0 }}>
          {(instructions || []).map((step, i) => (
            <li key={i} style={{ marginBottom: 4 }}>{step}</li>
          ))}
        </ol>
      ) : '—'}
    </Field>
    <Field label="Tips">
      {(tips || []).length > 0 ? (
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          {(tips || []).map((tip, i) => (
            <li key={i} style={{ marginBottom: 4 }}>{tip}</li>
          ))}
        </ul>
      ) : '—'}
    </Field>
  </div>
);

export const ExerciseShow: React.FC = () => {
  const { token } = theme.useToken();
  const { locales } = useActiveLocales();
  const { queryResult } = useShow<ExerciseDto>({ resource: 'exercises' });
  const item = queryResult?.data?.data;
  const [activeLang, setActiveLang] = useState('en');

  const translationLocales = useMemo(() => {
    const fromData = Object.keys(item?.translations || {}).filter((c) => !isDefaultLocale(c));
    const fromApi = locales.map((l) => l.code).filter((c) => !isDefaultLocale(c));
    return Array.from(new Set([...fromApi, ...fromData]));
  }, [item?.translations, locales]);

  const localeLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const l of locales) map[l.code] = l.label;
    const defaultLocale = locales.find((l) => isDefaultLocale(l.code));
    map.en = defaultLocale?.label || 'English';
    return map;
  }, [locales]);

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  const tabItems = [
    {
      key: 'en',
      label: localeLabels.en,
      children: (
        <LanguageContent
          name={item.name}
          description={item.description}
          instructions={item.instructions}
          tips={item.tips}
        />
      ),
    },
    ...translationLocales.map((code) => {
      const data = (item.translations?.[code] || {}) as ExerciseTranslationDto;
      return {
        key: code,
        label: localeLabels[code] || code,
        children: (
          <LanguageContent
            name={data.name}
            description={data.description}
            instructions={data.instructions}
            tips={data.tips}
          />
        ),
      };
    }),
  ];

  return (
    <Show resource="exercises">
      <div style={{ padding: '8px 0' }}>
        <Tabs
          activeKey={activeLang}
          onChange={setActiveLang}
          items={tabItems}
          style={{ marginBottom: 24 }}
        />

        <div
          style={{
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            paddingTop: 16,
          }}
        >
          <Field label="Muscle Groups">
            <Space size={[4, 4]} wrap>
              {item.muscleGroups?.map((g, i) => (
                <Tag key={g} style={tagStyle(muscleGroupColors[g] || muscleGroupColorList[i % muscleGroupColorList.length])}>
                  {g}
                </Tag>
              ))}
            </Space>
          </Field>
          {item.equipmentId && <Field label="Equipment ID">{item.equipmentId}</Field>}
          <Field label="Difficulty Level">
            <Tag style={tagStyle(difficultyColors[item.difficultyLevel] || '#898989')}>{item.difficultyLevel}</Tag>
          </Field>
          {item.videoUrl && (
            <Field label="Video">
              <a href={item.videoUrl} target="_blank" rel="noreferrer">{item.videoUrl}</a>
            </Field>
          )}
          {item.imageUrl && (
            <Field label="Image">
              <a href={item.imageUrl} target="_blank" rel="noreferrer">{item.imageUrl}</a>
            </Field>
          )}
          {item.caloriesPerRep != null && <Field label="Calories Per Rep">{item.caloriesPerRep}</Field>}
          <Field label="Compound">
            <Tag style={tagStyle(item.isCompound ? '#1890ff' : '#898989')}>
              {item.isCompound ? 'Yes' : 'No'}
            </Tag>
          </Field>
          <Field label="Active">
            <Tag style={tagStyle(item.isActive ? '#3ecf8e' : '#898989')}>
              {item.isActive ? 'Yes' : 'No'}
            </Tag>
          </Field>
          <Field label="Exercise Type">{item.exerciseType || '—'}</Field>
          <Field label="Movement Pattern">{item.movementPattern || '—'}</Field>
          <Field label="Measurement Type">{item.measurementType || '—'}</Field>
          <Field label="Primary Area">
            <Space size={[4, 4]} wrap>
              {item.primaryArea && <Tag style={tagStyle('#722ed1')}>{item.primaryArea}</Tag>}
              {(item.secondaryAreas ?? []).map((a) => (
                <Tag key={a} style={tagStyle('#898989')}>{a}</Tag>
              ))}
              {!item.primaryArea && '—'}
            </Space>
          </Field>
          {item.externalId && <Field label="External ID">{item.externalId}</Field>}
          {item.repRangeMin != null || item.repRangeMax != null ? (
            <Field label="Rep Range">{[item.repRangeMin ?? '?', item.repRangeMax ?? '?'].join(' – ')}</Field>
          ) : null}
          {item.durationRangeMinSec != null || item.durationRangeMaxSec != null ? (
            <Field label="Duration Range (sec)">{[item.durationRangeMinSec ?? '?', item.durationRangeMaxSec ?? '?'].join(' – ')}</Field>
          ) : null}
          {item.requiresSpace && <Field label="Requires Space">{item.requiresSpace}</Field>}
          {item.speedRangeMin != null || item.speedRangeMax != null ? (
            <Field label="Speed Range (km/h)">{[item.speedRangeMin ?? '?', item.speedRangeMax ?? '?'].join(' – ')}</Field>
          ) : null}
          {item.equipmentNeeded && <Field label="Equipment Needed">{item.equipmentNeeded}</Field>}
          {item.notes && (
            <Field label="Notes">
              <div style={{ whiteSpace: 'pre-wrap' }}>{item.notes}</div>
            </Field>
          )}
          <Field label="Created At">{new Date(item.createdAt).toLocaleString()}</Field>
          <Field label="Updated At">{new Date(item.updatedAt).toLocaleString()}</Field>
        </div>
      </div>
    </Show>
  );
};
